import React from 'react';
import ArticleLayout, { ArticleSection, ArticleSubSection } from '../layout/ArticleLayout';
import BlockRenderer, { resolveImageSrc } from '../layout/BlockRenderer';

// Renders a page_contents document (content_type "article") through ArticleLayout.
// Shared by the public article pages (fetched content) and the admin editor's live
// preview (in-memory, unsaved content), so "what you see in preview" is exactly
// what the public page will render.
// Many docx-converted docs (Articles and How It Works topics alike) carry a
// "References" section (source docx house style runs What Is X? through References)
// -- it's dropped entirely, from both the TOC and the rendered body, for every
// content_type. Relevant articles/topics linked from a References section's own
// hand-authored link_list blocks are instead surfaced via the dedicated Related
// Articles/Related Topics components below the content.
const isReferencesSection = (section) => /^references?$/i.test((section.title || '').trim());

const ArticleContentRenderer = ({ content, onDownloadPdf, backTo, backLabel, favorite }) => {
  const sections = (content.sections || []).filter((section) => !isReferencesSection(section));
  const toc = sections.map((section) => ({
    id: section.section_id,
    label: `${section.number ? `${section.number}. ` : ''}${section.title}`,
    subs: (section.subsections || []).map((sub) => ({
      id: sub.section_id,
      label: `${sub.number ? `${sub.number} ` : ''}${sub.title}`,
    })),
  }));

  return (
    <ArticleLayout
      category={content.category}
      title={content.title}
      subtitle={content.subtitle}
      description={content.description}
      readingTime={content.reading_time}
      date={content.date}
      coverImage={content.cover_image ? resolveImageSrc(content.cover_image) : null}
      toc={toc}
      onDownloadPdf={onDownloadPdf}
      favorite={favorite}
      backTo={backTo}
      backLabel={backLabel}
    >
      {sections.map((section, i) => (
        <ArticleSection
          key={section.section_id}
          id={section.section_id}
          number={section.number || String(i + 1)}
          title={section.title}
        >
          <BlockRenderer blocks={section.blocks} />
          {(section.subsections || []).map((sub) => (
            <ArticleSubSection key={sub.section_id} id={sub.section_id} number={sub.number} title={sub.title}>
              <BlockRenderer blocks={sub.blocks} />
            </ArticleSubSection>
          ))}
        </ArticleSection>
      ))}
    </ArticleLayout>
  );
};

export default ArticleContentRenderer;
